import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { BadgeCheck, Phone, Mail, Globe, Shield, ShieldOff, Search, ChevronDown, ChevronUp } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface Trade {
  id: number;
  userId: number;
  businessName: string;
  name: string | null;
  categories: string[];
  serviceAreas: string[];
  phone: string | null;
  email: string | null;
  website: string | null;
  verified: boolean;
  rating: number;
  reviewCount: number;
  emergencyCallout: boolean;
  yearsExperience: number | null;
  createdAt: string;
  userEmail: string | null;
  userName: string | null;
}

function StarRow({ rating, count }: { rating: number; count: number }) {
  return (
    <span className="text-xs text-slate-500">
      {"★".repeat(Math.round(rating))}{"☆".repeat(5 - Math.round(rating))} {count > 0 ? `(${count})` : "No reviews"}
    </span>
  );
}

export default function AdminTradespeople() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetch("/api/admin/trades", { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setTrades(d.trades ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (isLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  // Category stats
  const categoryMap: Record<string, number> = {};
  trades.forEach(t => t.categories.forEach(c => { categoryMap[c] = (categoryMap[c] ?? 0) + 1; }));
  const sortedCategories = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);

  const filtered = trades.filter(t => {
    const matchSearch = !search || [t.businessName, t.name, t.email, t.userEmail, t.phone].some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchCat = categoryFilter === "all" || t.categories.includes(categoryFilter);
    return matchSearch && matchCat;
  });

  async function toggleVerify(trade: Trade) {
    setActionLoading(trade.id);
    try {
      const res = await fetch(`/api/admin/trades/${trade.id}/verify`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ verified: !trade.verified }),
      });
      if (!res.ok) throw new Error();
      setTrades(prev => prev.map(t => t.id === trade.id ? { ...t, verified: !t.verified } : t));
      toast({ title: !trade.verified ? "Trade verified ✓" : "Verification removed" });
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  }

  function toggleExpand(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const contactEmail = (email: string | null, name: string) => {
    if (!email) return;
    window.location.href = `mailto:${email}?subject=Ayrshire Hub Tradespeople&body=Hi ${name},`;
  };

  return (
    <AdminLayout current="/admin/trades">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Tradespeople</h1>
            <p className="text-sm text-slate-500 mt-1">{trades.length} registered trades across Ayrshire</p>
          </div>
          <span className="text-sm font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary">
            {trades.filter(t => t.verified).length} verified
          </span>
        </div>

        {/* Category breakdown */}
        <div>
          <h2 className="text-sm font-semibold text-slate-600 mb-2">By Category</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategoryFilter("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${categoryFilter === "all" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}
            >
              All ({trades.length})
            </button>
            {sortedCategories.map(([cat, cnt]) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${categoryFilter === cat ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}
              >
                {cat} ({cnt})
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            aria-label="Search by name, email, phone"
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-3xl mb-2">🔧</p>
              <p className="font-semibold text-slate-700">No tradespeople found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(t => {
              const isExpanded = expanded.has(t.id);
              const contactEmail_ = t.email ?? t.userEmail;
              return (
                <Card key={t.id} className={t.verified ? "border-emerald-200" : ""}>
                  <CardContent className="pt-4 pb-3">
                    {/* Top row */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-lg">🔧</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-slate-800 text-sm">{t.businessName}</span>
                          {t.verified && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                              <BadgeCheck size={10} /> Verified
                            </span>
                          )}
                          {t.emergencyCallout && (
                            <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">24/7</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {t.categories.map(c => (
                            <span key={c} className="text-[10px] font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>
                          ))}
                        </div>
                        <StarRow rating={t.rating} count={t.reviewCount} />
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {contactEmail_ && (
                          <button
                            onClick={() => contactEmail(contactEmail_, t.name ?? t.businessName)}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-primary transition-colors"
                            title={`Email ${contactEmail_}`}
                          >
                            <Mail size={14} />
                          </button>
                        )}
                        {t.phone && (
                          <a
                            href={`tel:${t.phone}`}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-primary transition-colors"
                            title={`Call ${t.phone}`}
                          >
                            <Phone size={14} />
                          </a>
                        )}
                        {t.website && (
                          <a
                            href={t.website.startsWith("http") ? t.website : `https://${t.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-primary transition-colors"
                          >
                            <Globe size={14} />
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={actionLoading === t.id}
                          onClick={() => toggleVerify(t)}
                          className={`h-8 text-xs ${t.verified ? "text-slate-500 border-slate-200" : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"}`}
                        >
                          {t.verified ? <><ShieldOff size={11} className="mr-1" /> Unverify</> : <><Shield size={11} className="mr-1" /> Verify</>}
                        </Button>
                        <button
                          onClick={() => toggleExpand(t.id)}
                          className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-400 transition-colors"
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-600">
                        <div><span className="font-medium text-slate-400">Account email:</span> {t.userEmail ?? "—"}</div>
                        <div><span className="font-medium text-slate-400">Profile email:</span> {t.email ?? "—"}</div>
                        <div><span className="font-medium text-slate-400">Phone:</span> {t.phone ?? "—"}</div>
                        <div><span className="font-medium text-slate-400">Experience:</span> {t.yearsExperience ? `${t.yearsExperience} years` : "—"}</div>
                        <div><span className="font-medium text-slate-400">Service areas:</span> {t.serviceAreas.join(", ") || "—"}</div>
                        <div><span className="font-medium text-slate-400">Joined:</span> {new Date(t.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                        {t.website && (
                          <div className="col-span-2"><span className="font-medium text-slate-400">Website:</span> <a href={t.website.startsWith("http") ? t.website : `https://${t.website}`} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t.website}</a></div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
